import { json } from "@tanstack/react-start"

import type { Range } from "@repo/utils/types"

/**
 * Helper utility used to extract the domain from the request even if it's
 * behind a proxy. This is useful for sitemaps and other things.
 * @param request Request object
 * @returns Current domain
 * Util taken from https://github.com/forge-42/base-stack/blob/main/app/utils/http.ts
 */
export const createDomain = (request: Request) => {
  const headers = request.headers
  const maybeProto = headers.get("x-forwarded-proto")
  const maybeHost = headers.get("host")
  const url = new URL(request.url)

  // If the request is behind a proxy, we need to use the x-forwarded-proto and host headers
  // to get the correct domain
  if (maybeProto) {
    return `${maybeProto}://${maybeHost ?? url.host}`
  }

  // If we are in local development, return the localhost
  if (url.hostname === "localhost") {
    return `http://${url.host}`
  }

  // If we are in production, return the production domain
  return `https://${url.host}`
}

const ABSOLUTE_URL_REGEX = /^(https?:){0,1}\/\//

export function isAbsoluteUrl(url: string) {
  return ABSOLUTE_URL_REGEX.test(url)
}

export function successObject<Data extends object>(data: Data) {
  return { success: true, data } as const
}

export function errorObject<Data = object>(
  message: string,
  data?: Data | null,
) {
  return { success: false, message, data } as const
}

export function badRequestResponse<Data = object>(
  message: string,
  status: Range<400, 499>,
  data?: Data | null,
): never {
  throw json(errorObject(message, data), { status })
}

export function serverErrorResponse<Data = object>(
  message: string,
  status: Range<500, 599>,
  data?: Data | null,
): never {
  throw json(errorObject(message, data), { status })
}

export function successResponse<Data extends object>(data: Data) {
  return json(successObject(data))
}

export function errorResponse<Data = object>(
  message: string,
  data?: Data | null,
) {
  return json(errorObject(message, data))
}
